/**
 * Canonical product brand for NerveDX.
 * Single source of truth — consumed via @nervedx/shared/product-brand.
 */

export type ProductBrand = {
  productName: string;
  shortName: string;
  clinicalPlatformName: string;
  controlPlaneName: string;
  controlPlaneOperationsLabel: string;
  defaultLogoAlt: string;
  portalLogoAlt: string;
  lockScreenDisplayName: string;
  supportEmail: string;
  supportLabel: string;
  emailFromName: string;
};

function readEnv(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env?.[name]) {
    return process.env[name];
  }
  return undefined;
}

export const PRODUCT_BRAND: ProductBrand = {
  productName: "NerveDX",
  shortName: "NerveDX",
  clinicalPlatformName: "NerveDX Clinical Platform",
  controlPlaneName: "Control Plane",
  controlPlaneOperationsLabel: "Platform Operations",
  defaultLogoAlt: "NerveDX logo",
  portalLogoAlt: "NerveDX Clinical Portal Logo",
  lockScreenDisplayName: "NerveDX Clinical Portal",
  supportEmail: "support@nervedx.com",
  supportLabel: "NerveDX Support",
  emailFromName: "NerveDX EEG System",
};

export function getProductBrand(
  overrides?: Partial<ProductBrand>,
): ProductBrand {
  return {
    ...PRODUCT_BRAND,
    ...(overrides || {}),
    productName:
      String(
        overrides?.productName ||
          readEnv("PRODUCT_NAME") ||
          PRODUCT_BRAND.productName,
      ).trim() || PRODUCT_BRAND.productName,
    shortName:
      String(
        overrides?.shortName ||
          readEnv("PRODUCT_SHORT_NAME") ||
          PRODUCT_BRAND.shortName,
      ).trim() || PRODUCT_BRAND.shortName,
    supportEmail:
      String(
        overrides?.supportEmail ||
          readEnv("SUPPORT_EMAIL_FALLBACK") ||
          readEnv("VITE_SUPPORT_EMAIL_FALLBACK") ||
          PRODUCT_BRAND.supportEmail,
      ).trim() || PRODUCT_BRAND.supportEmail,
  };
}

const NERVEDX_NAME_PATTERN = /^nervedx\b/i;

export function normalizeProductBrandDisplayName(name: string): string {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    return trimmed;
  }
  if (NERVEDX_NAME_PATTERN.test(trimmed)) {
    return trimmed.replace(NERVEDX_NAME_PATTERN, PRODUCT_BRAND.productName);
  }
  return trimmed;
}
